import { MandatoryQueryParameterTooLongException } from '../exceptions/mandatoryQueryParameterTooLongException';
import { QueryParameterTooLongException } from '../exceptions/queryParameterTooLongException';
import { filterSplit } from './array';
import { getKeys } from './recordUtilities';

/** Mapper type for each key of request options to sendable type. */
export type ParameterifyFunctionMapper<D> = {
  [K in keyof D]-?: (val: D[K]) => string | string[] | undefined;
};

/**
 * Defines how to convert options to query.
 * @template QO The query options.
 */
export abstract class QueryOptionsHandler<QO> {
  private _queryOptions: QO;

  /**
   * Constructs the {@link QueryOptionsHandler}.
   * @param queryOptions The raw options.
   */
  public constructor(queryOptions: QO) {
    this._queryOptions = queryOptions;
  }

  /**
   * @returns The search parameters for the URL.
   */
  public toQueryParams(): URLSearchParams {
    const urlSearchParams = new URLSearchParams();
    const mapper = this.stringMapper();

    for (const key of getKeys(this._queryOptions)) {
      const param = mapper[key](this._queryOptions[key]);
      if (param !== undefined) {
        if (typeof param === 'string') {
          urlSearchParams.append(key.toString(), param);
        } else {
          for (const el of param) {
            urlSearchParams.append(key.toString(), el);
          }
        }
      }
    }

    return urlSearchParams;
  }

  /**
   *
   * @param charLimit The maximum characters count, one query param can use.
   * @throws {QueryParameterTooLongException} if one of the splittable query parameter does not enter alone (with the mandatory parameters).
   * @throws {MandatoryQueryParameterTooLongException} if the mandatory query parameters (not splittable) alone does not fit the char limitation.
   */
  public toMultipleQueryParams(
    charLimit: number,
    splitParameter: keyof QO
  ): URLSearchParams[] {
    const mapper = this.stringMapper();

    // Flatten params (including params that translate into collection)
    const keysAndParams: [string, string][] = getKeys(this._queryOptions)
      .map<[string, string][]>((key) => {
        const value = mapper[key](this._queryOptions[key]);
        if (value === undefined) return [];
        const keyStr = key.toString();
        if (typeof value === 'string') return [[keyStr, value]];
        return value.map((el) => [keyStr, el]);
      })
      .flatMap((el) => el);

    const splitParameterStr = splitParameter.toString();
    const [mandatoryKeysAndParams, splittableKeysAndParams] = filterSplit(
      keysAndParams,
      ([key]) => key !== splitParameterStr
    );

    const mandatoryUrlSearchParams = new URLSearchParams();

    for (const [key, param] of mandatoryKeysAndParams) {
      mandatoryUrlSearchParams.append(key, param);
    }

    if (mandatoryUrlSearchParams.toString().length > charLimit) {
      throw new MandatoryQueryParameterTooLongException();
    }

    let currentUrlSearchParams = new URLSearchParams(mandatoryUrlSearchParams);
    const urlSearchParamsCollection = [currentUrlSearchParams];

    let insertedCount = 0;
    let i = 0;

    while (i < splittableKeysAndParams.length) {
      const [key, param] = splittableKeysAndParams[i];
      currentUrlSearchParams.append(key.toString(), param);
      if (currentUrlSearchParams.toString().length > charLimit) {
        if (insertedCount === 0) {
          throw new QueryParameterTooLongException(
            key.toString(),
            param,
            charLimit
          );
        }

        const values = currentUrlSearchParams
          .getAll(key)
          .filter((el) => el !== param);
        currentUrlSearchParams.delete(key);

        for (const value of values) {
          currentUrlSearchParams.append(key, value);
        }
        currentUrlSearchParams = new URLSearchParams(mandatoryUrlSearchParams);
        urlSearchParamsCollection.push(currentUrlSearchParams);
        insertedCount = 0;
      } else {
        ++i;
        ++insertedCount;
      }
    }

    return urlSearchParamsCollection.filter(
      (params) => Array.from(params).length > 0
    );
  }

  /**
   * @returns The string mapper specifying the way each field should be converted to string.
   */
  protected abstract stringMapper(): ParameterifyFunctionMapper<QO>;
}
